<template>
  <!--
    หน้าล็อกเต็มจอ — คลุมทุก layout ของพนักงาน (back-office / POS / KDS)
    ล็อกแล้ว = ไม่มีทางข้าม (แบบ Netflix — ../silver-spoon-api/docs/license-expiry.md §7.1)
    ร้านได้แถบเตือน + popup ทุกครั้งที่เปิดแอป (LicenseReminder) มาตลอดช่วงใกล้หมดและผ่อนผัน 7 วันแล้ว
    จอนี้จึงมีแค่ทางต่ออายุ (LINE เป็นหลัก) · ตรวจสอบสถานะอีกครั้ง · ออกจากระบบ

    ตัวบังคับจริงอยู่ที่ BE (403 ERROR_LICENSE_EXPIRED ทุกเส้นยกเว้น login / logout / profile)
    จอนี้มีหน้าที่อธิบายว่าเกิดอะไรขึ้นและต้องทำยังไงต่อ
  -->
  <div class="license-lock">
    <div class="license-lock__card">
      <div class="license-lock__icon">
        <i class="fa-solid fa-lock" />
      </div>

      <h4 class="license-lock__title">{{ $t('_license.System license has expired') }}</h4>

      <p class="license-lock__lead">
        {{ $t('_license.Please contact support to renew') }}
      </p>

      <!-- แพ็ก · วันหมดอายุ — ร้านที่ไม่มีแพ็ก (ลืม start) ไม่มีทั้งคู่ บรรทัดนี้หายไปเอง -->
      <div v-if="license.planName || license.expiresOn" class="license-lock__meta">
        <span v-if="license.planName">
          <i class="fa-solid fa-crown me-1" />{{ $t('_plan.Current plan') }} <strong>{{ planLabel(license.planName) }}</strong>
        </span>
        <span v-if="license.planName && license.expiresOn"> · </span>
        <span v-if="license.expiresOn">
          {{ $t('_license.Expired on') }} <strong>{{ formatLicenseDate(license.expiresOn) }}</strong>
        </span>
      </div>

      <license-contact />

      <div class="license-lock__actions">
        <button
          type="button"
          class="license-lock__action"
          :disabled="checking"
          @click="checkAgain"
        >
          <span v-if="checking" class="spinner-border spinner-border-sm me-2" />
          <i v-else class="fa-solid fa-rotate-right me-2" />
          {{ $t('_license.Check status again') }}
        </button>

        <button type="button" class="license-lock__action license-lock__action--muted" @click="logout">
          <i class="fa-solid fa-right-from-bracket me-2" />{{ $t('Logout') }}
        </button>
      </div>

      <!-- ผลการตรวจสอบโชว์บนการ์ดเอง — toast อยู่ z-index 9999 ถูกหน้าล็อก (20000) บังมองไม่เห็น -->
      <div v-if="checkMessage" class="license-lock__check" role="status">{{ checkMessage }}</div>

      <div class="license-lock__hint">
        {{ $t('_license.Your data is safe - everything comes back once renewed') }}
      </div>
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import { useLicenseStore } from '@/stores/license.store';
import { useAuthStore } from '@/stores/auth.store';
import { Auth } from '@/services/endpoint/auth-api.service';
import LicenseContact from './LicenseContact.vue';
import { planLabel, formatLicenseDate } from '@/utils/licenseDisplay';

export default {
  name: 'LicenseLockScreen',
  components: { LicenseContact },
  data() {
    return {
      checking: false,
      checkMessage: ''
    };
  },
  computed: {
    license() {
      return useLicenseStore();
    }
  },
  methods: {
    planLabel,
    formatLicenseDate,
    /**
     * ยิง /v1/profile (เส้นที่ผ่าน allowlist ตอนล็อก) แล้วให้ interceptor อ่าน header
     * อัปเดต store ให้เอง — พอเราต่ออายุให้ ร้านกดปุ่มเดียวก็ใช้ต่อได้ ไม่ต้องสอน hard-refresh
     */
    async checkAgain() {
      this.checking = true;
      this.checkMessage = '';
      try {
        const res = await Auth.getProfile();
        // profile แนบ license มาแล้ว — ต่ออายุพร้อมเปลี่ยนแพ็ก = ได้เมนูชุดใหม่ทันที ไม่ต้องล็อกอินใหม่
        this.license.applyPlan(res?.data?.license);
        if (this.license.isLocked) {
          this.checkMessage = this.$t('_license.Still expired - please contact support');
        } else {
          // หน้าล็อกหายแล้ว toast จึงมองเห็น
          this.$toast.success(this.$t('_license.License is active again'));
        }
      } catch (err) {
        this.checkMessage = this.$t('_license.Could not check status - try again');
      } finally {
        this.checking = false;
      }
    },

    /** เครื่องที่ใช้ร่วมกันต้องออกได้ — logout อยู่ใน allowlist ของ LicenseMiddleware · ทำแบบเดียวกับเมนูบน (TopMenu) */
    async logout() {
      try {
        await Auth.logout();
      } catch (e) {
      } finally {
        Cookies.remove('token');
        useAuthStore().logout();
        location.reload();
      }
    }
  }
};
</script>

<style scoped>
.license-lock {
  position: fixed;
  inset: 0;
  z-index: 20000; /* เหนือ offcanvas/modal ของ bootstrap (สูงสุด 1055) */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.92);
  backdrop-filter: blur(3px);
}

.license-lock__card {
  width: 100%;
  max-width: 26rem;
  background: #fff;
  border-radius: 0.75rem;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, 0.35);
}

.license-lock__icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fdecea;
  color: #c0392b;
  font-size: 1.5rem;
}

.license-lock__title {
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.license-lock__lead {
  color: #64748b;
  margin-bottom: 1rem;
}

.license-lock__meta {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 1rem;
}

/* ปุ่มรอง 2 ปุ่ม คนละบรรทัด เต็มความกว้าง — กรอบชัดให้รู้ว่ากดได้ (โค้งเท่าปุ่มชุดต่ออายุด้านบน) */
.license-lock__actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

.license-lock__action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.625rem 1rem;
  border: 1px solid #93c5fd;
  border-radius: 0.75rem;
  background: #fff;
  color: #2563eb;
  font-size: 0.9375rem;
  font-weight: 600;
  transition: background 0.15s, border-color 0.15s;
}

.license-lock__action:hover:not(:disabled),
.license-lock__action:focus-visible {
  background: #eff6ff;
  border-color: #2563eb;
}

.license-lock__action:disabled {
  opacity: 0.65;
}

.license-lock__action--muted {
  border-color: #cbd5e1;
  color: #475569;
}

.license-lock__action--muted:hover:not(:disabled),
.license-lock__action--muted:focus-visible {
  background: #f8fafc;
  border-color: #94a3b8;
}

.license-lock__check {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.875rem;
}

.license-lock__hint {
  margin-top: 0.875rem;
  font-size: 0.75rem;
  color: #94a3b8;
}
</style>
